import { useEffect, useState }      from 'react'
import { useNavigate }               from 'react-router-dom'
import { useAuth }                   from '@/context/AuthContext'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import {
  listQuestionBanks, listGradingCriteria, createGradingCriteria,
  createExam, createQuestionBank, createQuestion, listSubjects,
} from '@/api/resources'
import { Button, Badge, FIELD_CLS, Spinner } from '@/components/ui/primitives'
import RichTextEditor from '@/components/ui/RichTextEditor'
import { ArrowLeft, Plus, Trash2, X } from 'lucide-react'
import { fetchAllPages } from '@/utils/fetchAll'

// ─────────────────────────────────────────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────────────────────────────────────────

function newOption(id) { return { id, text: '' } }

function newInlineQ() {
  return {
    _id: Date.now() + Math.random(),
    content: '', question_type: 1,
    options: [newOption(1), newOption(2), newOption(3), newOption(4)],
    correct_single: 1, correct_multi: [], correct_tf: true,
    short_text_answers: [''],
  }
}

function newInlineSection() {
  return { _id: Date.now() + Math.random(), subject: '', score: 1, questions: [newInlineQ()] }
}

const INLINE_TYPES = [
  { value: 1, label: "Bir javobli (radio)" },
  { value: 2, label: "Ko'p javobli (checkbox)" },
  { value: 3, label: "To'g'ri / Noto'g'ri" },
  { value: 4, label: "Qisqa matn" },
  { value: 5, label: "Insho" },
]

function buildInlinePayload(q) {
  const opts = q.options.filter(o => o.text.trim())
  switch (q.question_type) {
    case 1: return { question_type: 1, content: q.content, options: opts, correct_answer: { answer: q.correct_single } }
    case 2: return { question_type: 2, content: q.content, options: opts, correct_answer: { answers: q.correct_multi } }
    case 3: return { question_type: 3, content: q.content, options: [], correct_answer: { answer: q.correct_tf } }
    case 4: return { question_type: 4, content: q.content, options: [], correct_answer: { answers: (q.short_text_answers ?? []).filter(Boolean) } }
    default: return { question_type: q.question_type, content: q.content, options: [], correct_answer: {} }
  }
}

// National cert initializers
function initSectionA() {
  return Array.from({ length: 32 }, (_, i) => ({
    _id: `a_${i}`, number: i + 1,
    content: '', options: ['', '', '', ''], correct: 0, score: 1,
  }))
}
function initSectionB() {
  return {
    sharedOptions: Array.from({ length: 6 }, (_, i) => ({ letter: String.fromCharCode(65 + i), text: '' })),
    questions: Array.from({ length: 3 }, (_, i) => ({ _id: `b_${i}`, number: 33 + i, content: '', correct: 0 })),
    score: 1.5,
  }
}
function initSectionC() {
  return Array.from({ length: 10 }, (_, i) => ({
    _id: `c_${i}`, number: 36 + i,
    stem: '', subA: '', answerA: '', subB: '', answerB: '', score: 2,
  }))
}

const FIELD_S = {
  border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--ink)',
  borderRadius: 10, padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', width: '100%',
}

// ─────────────────────────────────────────────────────────────────────────────
// ExamTypePicker
// ─────────────────────────────────────────────────────────────────────────────

function ExamTypePicker({ onSelect }) {
  return (
    <div className="space-y-4 max-w-xl mx-auto py-4">
      <p className="text-sm" style={{ color: 'var(--ink-3)' }}>Imtihon turini tanlang:</p>

      {[
        {
          key: 'regular', icon: '📝', title: 'Oddiy imtihon',
          desc: "Savollar bazasidan tanlash yoki yangi savollar yaratish imkoniyati bilan. Reyting, ommaviylik va boshqa sozlamalar mavjud.",
          accent: 'coral',
        },
        {
          key: 'national', icon: '🎓', title: 'Milliy sertifikat',
          desc: "32 ta MCQ + 3 ta moslashtirish + 10 ta juftlik savol (AI baholash). Reyting chiqmaydi. Rasmiy sertifikat beriladi.",
          accent: 'lav',
        },
      ].map(({ key, icon, title, desc, accent }) => (
        <button key={key} type="button" onClick={() => onSelect(key)}
          className="w-full flex items-start gap-4 p-5 rounded-2xl border-2 text-left transition-all"
          style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = `var(--${accent})`; e.currentTarget.style.background = `var(--${accent}-tint)` }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--surface)' }}
        >
          <span className="text-3xl mt-0.5 select-none">{icon}</span>
          <div>
            <p className="font-semibold text-[15px]" style={{ color: 'var(--ink)' }}>{title}</p>
            <p className="text-sm mt-1" style={{ color: 'var(--ink-3)' }}>{desc}</p>
          </div>
        </button>
      ))}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared UI
// ─────────────────────────────────────────────────────────────────────────────

function ToggleCard({ label, hint, icon, checked, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className="w-full flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all"
      style={checked
        ? { borderColor: 'var(--coral)', background: 'var(--coral-tint)' }
        : { borderColor: 'var(--border)', background: 'transparent' }
      }
    >
      <span className="text-xl leading-none mt-0.5 select-none">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium" style={{ color: checked ? 'var(--coral)' : 'var(--ink-2)' }}>{label}</p>
        <p className="text-xs mt-0.5" style={{ color: 'var(--ink-3)' }}>{hint}</p>
      </div>
      <div className="relative flex-shrink-0 mt-0.5 w-9 h-5 rounded-full transition-colors"
        style={{ background: checked ? 'var(--coral)' : 'var(--border-strong)' }}>
        <span className="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all"
          style={{ left: checked ? '18px' : '2px' }} />
      </div>
    </button>
  )
}

function StepBar({ step, labels }) {
  return (
    <div className="flex items-center mb-8">
      {labels.map((label, i) => {
        const n = i + 1, done = n < step, current = n === step
        return (
          <div key={n} className={`flex items-center ${i < labels.length - 1 ? 'flex-1' : ''}`}>
            <div className="flex flex-col items-center gap-1">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                style={done
                  ? { background: 'var(--coral)', color: '#fff' }
                  : current
                    ? { background: 'var(--coral)', color: '#fff', boxShadow: '0 0 0 4px var(--coral-tint)' }
                    : { background: 'var(--bg-soft)', color: 'var(--ink-3)', border: '1px solid var(--border)' }
                }
              >{done ? '✓' : n}</div>
              <span className="text-xs whitespace-nowrap"
                style={{ color: current ? 'var(--coral)' : 'var(--ink-3)', fontWeight: current ? 600 : 400 }}>
                {label}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div className="flex-1 h-px mb-5 mx-2 transition-colors"
                style={{ background: done ? 'var(--coral-soft)' : 'var(--border)' }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Regular exam — InlineQuestionCard
// ─────────────────────────────────────────────────────────────────────────────

function InlineQuestionCard({ question: q, number, onChange, onRemove }) {
  const type = q.question_type
  const hasOptions = type === 1 || type === 2

  return (
    <div className="rounded-xl border" style={{ borderColor: 'var(--border)' }}>
      <div className="flex items-center gap-2 px-3 py-2.5 border-b rounded-t-xl"
        style={{ background: 'var(--bg-soft)', borderColor: 'var(--border)' }}>
        <span className="text-xs font-bold w-6 text-center" style={{ color: 'var(--ink-3)' }}>{number}</span>
        <select value={type} onChange={e => onChange({ question_type: Number(e.target.value) })}
          className="flex-1 py-1 px-2 rounded-lg border text-xs"
          style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--ink)' }}>
          {INLINE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <button type="button" onClick={onRemove}
          className="p-1 rounded transition-colors" style={{ color: 'var(--ink-3)' }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-3)'}>
          <X size={14} />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <RichTextEditor
          value={q.content}
          onChange={val => onChange({ content: val })}
          placeholder="Savol matnini kiriting..."
        />
        {hasOptions && (
          <div className="space-y-2">
            <p className="text-xs font-medium" style={{ color: 'var(--ink-3)' }}>
              {type === 1 ? "Variantlar (to'g'ri javobni belgilang):" : "Variantlar (bir nechtasini belgilang):"}
            </p>
            {q.options.map((opt, idx) => (
              <div key={opt.id} className="flex items-center gap-2">
                {type === 1
                  ? <input type="radio" name={`cr_${q._id}`} checked={q.correct_single === opt.id}
                      onChange={() => onChange({ correct_single: opt.id })} style={{ accentColor: 'var(--coral)', flexShrink: 0 }} />
                  : <input type="checkbox" checked={q.correct_multi.includes(opt.id)}
                      onChange={e => onChange({ correct_multi: e.target.checked ? [...q.correct_multi, opt.id] : q.correct_multi.filter(id => id !== opt.id) })}
                      style={{ accentColor: 'var(--coral)', flexShrink: 0 }} />
                }
                <input type="text" placeholder={`${idx + 1}-variant`} value={opt.text}
                  onChange={e => onChange({ options: q.options.map((o, i) => i === idx ? { ...o, text: e.target.value } : o) })}
                  className="flex-1 py-1.5 px-3 rounded-lg border text-sm"
                  style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--ink)' }} />
                <button type="button"
                  onClick={() => q.options.length > 2 && onChange({ options: q.options.filter((_, i) => i !== idx) })}
                  disabled={q.options.length <= 2}
                  className="p-1 disabled:opacity-30 transition-colors" style={{ color: 'var(--ink-3)' }}>
                  <X size={12} />
                </button>
              </div>
            ))}
            <button type="button"
              onClick={() => onChange({ options: [...q.options, newOption(Math.max(0, ...q.options.map(o => o.id)) + 1)] })}
              className="text-xs font-medium flex items-center gap-1" style={{ color: 'var(--coral)' }}>
              <Plus size={12} /> Variant qo'shish
            </button>
          </div>
        )}
        {type === 3 && (
          <div className="flex gap-6">
            {[{ v: true, l: "✅ To'g'ri" }, { v: false, l: "❌ Noto'g'ri" }].map(({ v, l }) => (
              <label key={String(v)} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name={`tf_${q._id}`} checked={q.correct_tf === v}
                  onChange={() => onChange({ correct_tf: v })} style={{ accentColor: 'var(--coral)' }} />
                <span className="text-sm">{l}</span>
              </label>
            ))}
          </div>
        )}
        {type === 4 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium" style={{ color: 'var(--ink-3)' }}>
                To'g'ri javob variantlari
                <span className="ml-1.5 font-normal" style={{ color: 'var(--ink-3)' }}>(AI barchasi bilan taqqoslaydi)</span>
              </p>
              <button
                type="button"
                onClick={() => onChange({ short_text_answers: [...(q.short_text_answers ?? ['']), ''] })}
                className="text-xs font-medium flex items-center gap-1"
                style={{ color: 'var(--coral)' }}
              >
                <Plus size={12} /> Variant qo'shish
              </button>
            </div>
            {(q.short_text_answers ?? ['']).map((ans, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="text-xs font-semibold w-5 text-center" style={{ color: 'var(--ink-3)' }}>{idx + 1}.</span>
                <input
                  type="text"
                  value={ans}
                  onChange={e => {
                    const next = [...(q.short_text_answers ?? [''])]
                    next[idx] = e.target.value
                    onChange({ short_text_answers: next })
                  }}
                  placeholder={idx === 0 ? "Asosiy to'g'ri javob" : `Variant ${idx + 1}`}
                  className="flex-1 py-1.5 px-3 rounded-lg border text-sm"
                  style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--ink)' }}
                />
                {(q.short_text_answers ?? ['']).length > 1 && (
                  <button
                    type="button"
                    onClick={() => onChange({ short_text_answers: (q.short_text_answers ?? ['']).filter((_, i) => i !== idx) })}
                    className="p-1 transition-colors"
                    style={{ color: 'var(--ink-3)' }}
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
            <p className="text-xs rounded-lg px-3 py-2" style={{ color: 'var(--ink-3)', background: 'var(--bg-soft)' }}>
              🤖 Aniq mos kelmasa AI semantik tekshiruv o'tkazadi.
            </p>
          </div>
        )}
        {type === 5 && (
          <p className="text-xs rounded-xl px-3 py-2" style={{ color: 'var(--ink-3)', background: 'var(--bg-soft)' }}>
            🤖 Insho — AI tomonidan baholanadi.
          </p>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// National cert — Section A (Q1–32 MCQ)
// ─────────────────────────────────────────────────────────────────────────────

const OPT_LABELS = ['A', 'B', 'C', 'D']

function NatMCQCard({ q, onChange }) {
  return (
    <div className="rounded-xl border" style={{ borderColor: 'var(--border)' }}>
      <div className="flex items-center gap-2 px-3 py-2 border-b rounded-t-xl"
        style={{ background: 'var(--bg-soft)', borderColor: 'var(--border)' }}>
        <span className="text-xs font-bold" style={{ color: 'var(--ink-3)' }}>{q.number}.</span>
        <span className="flex-1 text-xs" style={{ color: 'var(--ink-3)' }}>MCQ (4 variant)</span>
        <label className="text-xs" style={{ color: 'var(--ink-3)' }}>Ball:</label>
        <input type="number" min="0.5" step="0.5" value={q.score}
          onChange={e => onChange({ score: Number(e.target.value) || 1 })}
          className="py-0.5 px-2 rounded-lg border w-14 text-xs"
          style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--ink)' }} />
      </div>
      <div className="p-3 space-y-2.5">
        <RichTextEditor
          value={q.content}
          onChange={val => onChange({ content: val })}
          placeholder={`${q.number}-savol matnini kiriting...`}
          minHeight={80}
        />
        <div className="grid grid-cols-2 gap-2">
          {OPT_LABELS.map((letter, idx) => (
            <div key={letter} className="flex items-center gap-2">
              <input type="radio" name={`nat_a_${q._id}`} checked={q.correct === idx}
                onChange={() => onChange({ correct: idx })} style={{ accentColor: 'var(--coral)', flexShrink: 0 }} />
              <span className="text-xs font-bold w-5" style={{ color: 'var(--ink-3)' }}>{letter})</span>
              <input type="text" value={q.options[idx]}
                onChange={e => onChange({ options: q.options.map((o, i) => i === idx ? e.target.value : o) })}
                placeholder={`${letter} variant`}
                className="flex-1 py-1 px-2 rounded-lg border text-sm"
                style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--ink)' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// National cert — Section B (Q33–35 matching)
// ─────────────────────────────────────────────────────────────────────────────

const MATCH_LABELS = ['A', 'B', 'C', 'D', 'E', 'F']

function NatMatchSection({ sectionB, onChange }) {
  function setOpt(idx, text) {
    onChange({ sharedOptions: sectionB.sharedOptions.map((o, i) => i === idx ? { ...o, text } : o) })
  }
  function setQ(qIdx, patch) {
    onChange({ questions: sectionB.questions.map((q, i) => i === qIdx ? { ...q, ...patch } : q) })
  }

  return (
    <div className="space-y-4">
      {/* Shared options panel */}
      <div className="rounded-xl border p-4 space-y-2"
        style={{ borderColor: 'var(--butter-soft)', background: 'var(--butter-tint)' }}>
        <p className="text-sm font-semibold" style={{ color: 'var(--butter)' }}>
          Umumiy javob variantlari (A – F)
        </p>
        <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
          Bu 6 ta variant 33, 34, 35-savollarga mos keladi.
        </p>
        <div className="grid grid-cols-2 gap-2 pt-1">
          {sectionB.sharedOptions.map((opt, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <span className="text-sm font-bold w-6" style={{ color: 'var(--ink-2)' }}>{opt.letter})</span>
              <input type="text" value={opt.text} onChange={e => setOpt(idx, e.target.value)}
                placeholder={`${opt.letter} variant matni`}
                className="flex-1 py-1.5 px-3 rounded-lg border text-sm"
                style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--ink)' }} />
            </div>
          ))}
        </div>
      </div>

      {/* 3 questions */}
      <div className="space-y-3">
        {sectionB.questions.map((q, qIdx) => (
          <div key={q._id} className="rounded-xl border p-4 space-y-3"
            style={{ borderColor: 'var(--border)' }}>
            <p className="text-xs font-bold" style={{ color: 'var(--ink-3)' }}>
              {q.number}-savol
            </p>
            <RichTextEditor
              value={q.content}
              onChange={val => setQ(qIdx, { content: val })}
              placeholder={`${q.number}-savol matnini kiriting...`}
              minHeight={80}
            />
            <div>
              <p className="text-xs mb-2" style={{ color: 'var(--ink-3)' }}>To'g'ri javob (A–F dan tanlang):</p>
              <div className="flex flex-wrap gap-2">
                {MATCH_LABELS.map((letter, optIdx) => (
                  <button key={letter} type="button"
                    onClick={() => setQ(qIdx, { correct: optIdx })}
                    className="w-9 h-9 rounded-xl text-sm font-bold border-2 transition-all"
                    style={q.correct === optIdx
                      ? { background: 'var(--butter)', color: '#fff', borderColor: 'var(--butter)' }
                      : { borderColor: 'var(--border)', color: 'var(--ink-2)', background: 'transparent' }
                    }
                  >{letter}</button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Score per question */}
      <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--bg-soft)' }}>
        <span className="text-xs" style={{ color: 'var(--ink-3)' }}>Har bir savol uchun ball:</span>
        <input type="number" min="0.5" step="0.5" value={sectionB.score}
          onChange={e => onChange({ score: Number(e.target.value) || 1 })}
          className="py-1 px-2 rounded-lg border w-16 text-sm"
          style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--ink)' }} />
        <span className="text-xs" style={{ color: 'var(--ink-3)' }}>× 3 savol = <strong style={{ color: 'var(--ink)' }}>{(sectionB.score * 3).toFixed(1)}</strong> ball</span>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// National cert — Section C (Q36–45 paired, AI evaluated)
// ─────────────────────────────────────────────────────────────────────────────

function NatPairedCard({ q, onChange }) {
  return (
    <div className="rounded-xl border" style={{ borderColor: 'var(--border)' }}>
      <div className="flex items-center gap-2 px-3 py-2.5 border-b rounded-t-xl"
        style={{ background: 'var(--bg-soft)', borderColor: 'var(--border)' }}>
        <span className="text-xs font-bold" style={{ color: 'var(--ink-3)' }}>
          {q.number}-savol · juftlik · AI baholash
        </span>
        <label className="text-xs ml-auto" style={{ color: 'var(--ink-3)' }}>Ball:</label>
        <input type="number" min="0.5" step="0.5" value={q.score}
          onChange={e => onChange({ score: Number(e.target.value) || 1 })}
          className="py-0.5 px-2 rounded-lg border w-14 text-xs"
          style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--ink)' }} />
      </div>
      <div className="p-3 space-y-3">
        {/* Stem / context */}
        <RichTextEditor
          value={q.stem}
          onChange={val => onChange({ stem: val })}
          placeholder="Kontekst matni (kirish, tarix, matn parchasi...)"
          minHeight={80}
        />

        {/* Sub-question A */}
        <div className="space-y-2 p-3 rounded-xl" style={{ background: 'var(--mint-tint)' }}>
          <p className="text-xs font-semibold" style={{ color: 'var(--mint)' }}>a) qism</p>
          <input type="text" value={q.subA} onChange={e => onChange({ subA: e.target.value })}
            placeholder="a) savol matni..."
            className="w-full py-1.5 px-3 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--ink)' }} />
          <input type="text" value={q.answerA} onChange={e => onChange({ answerA: e.target.value })}
            placeholder="Kutilayotgan to'g'ri javob (AI shu bilan taqqoslaydi)..."
            className="w-full py-1.5 px-3 rounded-lg border text-sm"
            style={{ borderColor: 'var(--mint-soft)', background: 'var(--surface)', color: 'var(--ink)' }} />
        </div>

        {/* Sub-question B */}
        <div className="space-y-2 p-3 rounded-xl" style={{ background: 'var(--sky-tint)' }}>
          <p className="text-xs font-semibold" style={{ color: 'var(--sky)' }}>b) qism</p>
          <input type="text" value={q.subB} onChange={e => onChange({ subB: e.target.value })}
            placeholder="b) savol matni..."
            className="w-full py-1.5 px-3 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--ink)' }} />
          <input type="text" value={q.answerB} onChange={e => onChange({ answerB: e.target.value })}
            placeholder="Kutilayotgan to'g'ri javob (AI shu bilan taqqoslaydi)..."
            className="w-full py-1.5 px-3 rounded-lg border text-sm"
            style={{ borderColor: 'var(--sky-soft)', background: 'var(--surface)', color: 'var(--ink)' }} />
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Time + Criteria fields (shared between both forms)
// ─────────────────────────────────────────────────────────────────────────────

function TimeStep({ register, allCriteria, setCriteria, setValue, showNewCriteria, setShowNewCriteria, newCriteriaName, setNewCriteriaName, savingCriteria, setSavingCriteria }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="block text-sm font-medium" style={{ color: 'var(--ink-2)' }}>Boshlanish vaqti</label>
          <input {...register('start_time')} type="datetime-local" className={FIELD_CLS} />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium" style={{ color: 'var(--ink-2)' }}>Tugash vaqti</label>
          <input {...register('end_time')} type="datetime-local" className={FIELD_CLS} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="block text-sm font-medium" style={{ color: 'var(--ink-2)' }}>Vaqt chegarasi</label>
          <input {...register('time_limit_minutes')} type="number" min="0" className={FIELD_CLS} placeholder="daqiqa" />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium" style={{ color: 'var(--ink-2)' }}>Urinishlar soni</label>
          <input {...register('allowed_attempts')} type="number" min="1" className={FIELD_CLS} />
        </div>
      </div>
      <p className="text-xs rounded-xl px-3 py-2.5" style={{ color: 'var(--ink-3)', background: 'var(--bg-soft)' }}>
        ⏱ Vaqt chegarasi talaba imtihonni boshlagan paytdan hisoblanadi.
      </p>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium" style={{ color: 'var(--ink-2)' }}>Baholash mezoni</label>
          <button type="button" onClick={() => setShowNewCriteria(v => !v)}
            className="text-xs font-medium flex items-center gap-1" style={{ color: 'var(--coral)' }}>
            <Plus size={12} /> Yangi mezon
          </button>
        </div>
        <select {...register('grading_criteria')} className={FIELD_CLS}>
          <option value="">— Mezon tanlang —</option>
          {(allCriteria || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {showNewCriteria && (
          <div className="flex items-center gap-2 p-3 rounded-xl border"
            style={{ background: 'var(--coral-tint)', borderColor: 'var(--coral-soft)' }}>
            <input type="text" value={newCriteriaName} onChange={e => setNewCriteriaName(e.target.value)}
              placeholder="Yangi mezon nomi..." className={`${FIELD_CLS} flex-1`} />
            <button type="button" disabled={!newCriteriaName.trim() || savingCriteria}
              onClick={async () => {
                setSavingCriteria(true)
                try {
                  const { data } = await createGradingCriteria({ name: newCriteriaName.trim() })
                  setCriteria(prev => [...prev, data])
                  setValue('grading_criteria', data.id)
                  setNewCriteriaName(''); setShowNewCriteria(false)
                } catch (err) { alert(err.response?.data?.name?.[0] || err.message) }
                finally { setSavingCriteria(false) }
              }}
              className="px-3 py-2 text-sm rounded-xl font-semibold disabled:opacity-50 whitespace-nowrap"
              style={{ background: 'var(--coral)', color: '#fff' }}>
              {savingCriteria ? '...' : 'Saqlash'}
            </button>
            <button type="button" onClick={() => { setShowNewCriteria(false); setNewCriteriaName('') }}
              className="p-2" style={{ color: 'var(--ink-3)' }}>
              <X size={15} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// RegularExamForm
// ─────────────────────────────────────────────────────────────────────────────

const REG_STEPS = ['Asosiy', 'Vaqt', 'Savollar', "Ko'rib chiqish"]

function RegularExamForm({ allBanks, allSubjects, allCriteria, setCriteria, defaultCriteriaId, onSaved, onBack }) {
  const [questionMode,    setQuestionMode]    = useState(null)
  const [modeError,       setModeError]       = useState(false)
  const [inlineSections,  setInlineSections]  = useState([newInlineSection()])
  const [activeSection,   setActiveSection]   = useState(0)
  const [step,            setStep]            = useState(1)
  const [saveError,       setSaveError]       = useState(null)
  const [showNewCriteria, setShowNewCriteria] = useState(false)
  const [newCriteriaName, setNewCriteriaName] = useState('')
  const [savingCriteria,  setSavingCriteria]  = useState(false)

  const { register, handleSubmit, control, watch, trigger, setValue, formState: { isSubmitting, errors } } = useForm({
    shouldUnregister: false,
    defaultValues: {
      title: '', description: '',
      is_public: false, leaderboard_visible: false, show_errors: false,
      start_time: '', end_time: '',
      time_limit_minutes: '', allowed_attempts: 1,
      grading_criteria: defaultCriteriaId != null ? String(defaultCriteriaId) : '',
      bank_configs: [],
    },
  })
  const { fields, append, remove } = useFieldArray({ control, name: 'bank_configs' })
  const formValues  = watch()
  const bankConfigs = formValues.bank_configs || []
  const totalQ      = bankConfigs.reduce((s, bc) => s + (Number(bc.question_count) || 0), 0)
  const totalScore  = bankConfigs.reduce((s, bc) => s + (Number(bc.question_count) || 0) * (Number(bc.score_per_question) || 0), 0)
  const selectedCriteria = allCriteria?.find(c => String(c.id) === String(formValues.grading_criteria))

  async function onSubmit(data) {
    setSaveError(null)
    try {
      let bankCfgs = (data.bank_configs || []).map((bc, i) => ({
        question_bank: Number(bc.question_bank), question_count: Number(bc.question_count),
        score_per_question: Number(bc.score_per_question), order: i,
      }))
      if (questionMode === 'inline') {
        const validSections = inlineSections
          .map(sec => ({ ...sec, questions: sec.questions.filter(q => q.content.trim()) }))
          .filter(sec => sec.questions.length > 0)
        if (validSections.length === 0) { setSaveError('Kamida bitta savol kiriting.'); return }

        const subjNameCounts = {}
        const newBankCfgs = []
        for (let i = 0; i < validSections.length; i++) {
          const sec = validSections[i]
          const subjName = allSubjects.find(s => String(s.id) === String(sec.subject))?.name
          const label = subjName || `Fan ${i + 1}`
          subjNameCounts[label] = (subjNameCounts[label] || 0) + 1
          const dupSuffix = subjNameCounts[label] > 1 ? ` (${subjNameCounts[label]})` : ''
          const bankName = validSections.length > 1
            ? `${data.title} — ${label}${dupSuffix}`
            : `${data.title} — Savol bazasi`
          const { data: bank } = await createQuestionBank({
            name: bankName,
            subject: sec.subject ? Number(sec.subject) : null,
          })
          await Promise.all(sec.questions.map(q => createQuestion({ ...buildInlinePayload(q), question_bank: bank.id })))
          newBankCfgs.push({
            question_bank: bank.id, question_count: sec.questions.length,
            score_per_question: Number(sec.score) || 1, order: i,
          })
        }
        bankCfgs = newBankCfgs
      }
      await createExam({
        ...data,
        is_national_cert: false,
        time_limit_minutes: data.time_limit_minutes ? Number(data.time_limit_minutes) : null,
        allowed_attempts:   Number(data.allowed_attempts),
        start_time:         data.start_time || null,
        end_time:           data.end_time || null,
        grading_criteria:   data.grading_criteria ? Number(data.grading_criteria) : null,
        bank_configs:       bankCfgs,
      })
      onSaved()
    } catch (err) {
      setSaveError(err.response?.data ? JSON.stringify(err.response.data) : err.message)
    }
  }

  async function goNext() {
    if (step === 1) {
      const ok = await trigger('title'); if (!ok) return
      if (!questionMode) { setModeError(true); return }
      setModeError(false)
    }
    if (step === 3 && questionMode === 'bank' && fields.length > 0) {
      const keys = fields.flatMap((_, i) => [`bank_configs.${i}.question_bank`, `bank_configs.${i}.question_count`, `bank_configs.${i}.score_per_question`])
      const ok = await trigger(keys); if (!ok) return
    }
    setStep(s => s + 1)
  }

  return (
    <form onSubmit={e => e.preventDefault()} className="space-y-6">
      <StepBar step={step} labels={REG_STEPS} />

      {/* Step 1: Asosiy */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="block text-sm font-medium" style={{ color: 'var(--ink-2)' }}>Imtihon nomi *</label>
            <input {...register('title', { required: true })} className={FIELD_CLS} placeholder="Masalan: Matematika fanidan yakuniy imtihon" />
            {errors.title && <p className="text-xs" style={{ color: 'var(--danger)' }}>Majburiy maydon</p>}
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium" style={{ color: 'var(--ink-2)' }}>Tavsif</label>
            <textarea {...register('description')} rows={3} className={FIELD_CLS} placeholder="Imtihon haqida qisqacha ma'lumot..." />
          </div>
          <div className="space-y-2 pt-1">
            <Controller control={control} name="is_public" render={({ field }) => (
              <ToggleCard icon="🌐" label="Ommaviy imtihon" hint="Barcha foydalanuvchilar ko'ra oladi va ishtirok eta oladi" checked={field.value} onChange={field.onChange} />
            )} />
            <Controller control={control} name="leaderboard_visible" render={({ field }) => (
              <ToggleCard icon="🏆" label="Reyting ommaviy" hint="Top natijalar barcha talabalar uchun ko'rinadi" checked={field.value} onChange={field.onChange} />
            )} />
            <Controller control={control} name="show_errors" render={({ field }) => (
              <ToggleCard icon="🔍" label="Xatolarni ko'rsatish" hint="Imtihon tugagach o'quvchilar o'z xatolarini ko'ra oladi" checked={field.value} onChange={field.onChange} />
            )} />
          </div>

          {/* Mode picker embedded in step 1 */}
          <div className="space-y-2 pt-2">
            <p className="text-sm font-semibold" style={{ color: 'var(--ink-2)' }}>Savol kiritish usuli *</p>
            {modeError && <p className="text-xs" style={{ color: 'var(--danger)' }}>Iltimos, usulni tanlang</p>}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                { key: 'bank',   icon: '📚', title: 'Savollar bazasidan', desc: 'Mavjud bazani imtihonga ulash' },
                { key: 'inline', icon: '✏️', title: 'Hozir yaratish',     desc: 'Savollarni to\'g\'ridan-to\'g\'ri kiriting' },
              ].map(({ key, icon, title, desc }) => (
                <button key={key} type="button" onClick={() => { setQuestionMode(key); setModeError(false) }}
                  className="flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all"
                  style={questionMode === key
                    ? { borderColor: 'var(--coral)', background: 'var(--coral-tint)' }
                    : { borderColor: 'var(--border)', background: 'transparent' }
                  }
                >
                  <span className="text-2xl select-none">{icon}</span>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: questionMode === key ? 'var(--coral)' : 'var(--ink)' }}>{title}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--ink-3)' }}>{desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Vaqt */}
      {step === 2 && (
        <TimeStep
          register={register} allCriteria={allCriteria} setCriteria={setCriteria} setValue={setValue}
          showNewCriteria={showNewCriteria} setShowNewCriteria={setShowNewCriteria}
          newCriteriaName={newCriteriaName} setNewCriteriaName={setNewCriteriaName}
          savingCriteria={savingCriteria} setSavingCriteria={setSavingCriteria}
        />
      )}

      {/* Step 3: Savollar — bank */}
      {step === 3 && questionMode === 'bank' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-xl px-4 py-2.5 border"
            style={{ background: 'var(--coral-tint)', borderColor: 'var(--coral-soft)' }}>
            <span className="text-sm font-medium" style={{ color: 'var(--coral)' }}>
              Jami: <strong>{totalQ}</strong> savol · <strong>{totalScore}</strong> ball
            </span>
            <Button type="button" variant="secondary" size="sm"
              onClick={() => append({ question_bank: '', question_count: 10, score_per_question: 1, order: fields.length })}>
              <Plus size={14} /> Qo'shish
            </Button>
          </div>
          {fields.length === 0 && (
            <p className="text-sm text-center py-8" style={{ color: 'var(--ink-3)' }}>Savollar bazasi tanlanmagan</p>
          )}
          <div className="space-y-2">
            {fields.map((field, idx) => {
              const selId  = watch(`bank_configs.${idx}.question_bank`)
              const selBank = allBanks.find(b => String(b.id) === String(selId))
              const maxCount = selBank?.question_count
              return (
                <div key={field.id} className="flex items-end gap-2 p-3 rounded-xl border"
                  style={{ background: 'var(--bg-soft)', borderColor: 'var(--border)' }}>
                  <div className="flex-1 space-y-1">
                    <label className="block text-xs font-medium" style={{ color: 'var(--ink-3)' }}>Baza</label>
                    <select {...register(`bank_configs.${idx}.question_bank`, { required: true })} className={FIELD_CLS}>
                      <option value="">— Tanlang —</option>
                      {allBanks.map(b => <option key={b.id} value={b.id}>{b.name} ({b.question_count} savol)</option>)}
                    </select>
                  </div>
                  <div className="w-28 space-y-1">
                    <label className="block text-xs font-medium" style={{ color: 'var(--ink-3)' }}>
                      Soni {maxCount !== undefined && <span style={{ color: 'var(--muted)' }}>(max {maxCount})</span>}
                    </label>
                    <input {...register(`bank_configs.${idx}.question_count`, { required: true, min: 1, max: maxCount, valueAsNumber: true })}
                      type="number" min="1" max={maxCount}
                      className={`${FIELD_CLS}${errors?.bank_configs?.[idx]?.question_count ? ' border-red-400' : ''}`} />
                  </div>
                  <div className="w-24 space-y-1">
                    <label className="block text-xs font-medium" style={{ color: 'var(--ink-3)' }}>Ball</label>
                    <input {...register(`bank_configs.${idx}.score_per_question`, { required: true, min: 0.5 })} type="number" min="0.5" step="0.5" className={FIELD_CLS} />
                  </div>
                  <button type="button" onClick={() => remove(idx)}
                    className="p-2 transition-colors" style={{ color: 'var(--ink-3)' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-3)'}>
                    <Trash2 size={15} />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Step 3: Savollar — inline (fanlar orasida tab orqali o'tish) */}
      {step === 3 && questionMode === 'inline' && (
        <div className="space-y-4">
          {inlineSections.length > 1 && (
            <div className="rounded-xl px-4 py-2.5 border"
              style={{ background: 'var(--coral-tint)', borderColor: 'var(--coral-soft)' }}>
              <span className="text-sm font-medium" style={{ color: 'var(--coral)' }}>
                Jami: <strong>{inlineSections.reduce((s, sec) => s + sec.questions.filter(q => q.content.trim()).length, 0)}</strong> savol · {inlineSections.length} ta fan
              </span>
            </div>
          )}

          {/* Fan tablari */}
          <div className="flex items-center gap-1 border-b overflow-x-auto" style={{ borderColor: 'var(--border)' }}>
            {inlineSections.map((sec, si) => {
              const subjName = allSubjects.find(s => String(s.id) === String(sec.subject))?.name
              const validCount = sec.questions.filter(q => q.content.trim()).length
              const isActive = activeSection === si
              return (
                <div key={sec._id} className="flex items-center flex-none">
                  <button type="button" onClick={() => setActiveSection(si)}
                    className="px-3.5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors"
                    style={isActive
                      ? { borderColor: 'var(--coral)', color: 'var(--coral)' }
                      : { borderColor: 'transparent', color: 'var(--ink-3)' }}>
                    {subjName || `Fan ${si + 1}`}{validCount > 0 ? ` (${validCount})` : ''}
                  </button>
                  {inlineSections.length > 1 && (
                    <button type="button"
                      onClick={() => {
                        setInlineSections(prev => prev.filter((_, i) => i !== si))
                        setActiveSection(prev => Math.max(0, prev > si ? prev - 1 : prev === si ? 0 : prev))
                      }}
                      className="p-1 -ml-1 rounded transition-colors" style={{ color: 'var(--ink-3)' }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-3)'}>
                      <X size={13} />
                    </button>
                  )}
                </div>
              )
            })}
            <button type="button"
              onClick={() => { setInlineSections(prev => [...prev, newInlineSection()]); setActiveSection(inlineSections.length) }}
              className="px-3 py-2.5 text-sm font-semibold flex items-center gap-1 whitespace-nowrap flex-none transition-colors"
              style={{ color: 'var(--coral)' }}>
              <Plus size={14} /> Fan
            </button>
          </div>

          {/* Faol fan bo'limi */}
          {inlineSections[activeSection] && (
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-1 space-y-1">
                  <label className="block text-sm font-medium" style={{ color: 'var(--ink-2)' }}>Fan</label>
                  <select value={inlineSections[activeSection].subject}
                    onChange={e => setInlineSections(prev => prev.map((s, i) => i === activeSection ? { ...s, subject: e.target.value } : s))}
                    className={FIELD_CLS}>
                    <option value="">— Fan tanlanmagan —</option>
                    {allSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="w-28 space-y-1">
                  <label className="block text-sm font-medium" style={{ color: 'var(--ink-2)' }}>Ball</label>
                  <input type="number" min="0.5" step="0.5" value={inlineSections[activeSection].score}
                    onChange={e => setInlineSections(prev => prev.map((s, i) => i === activeSection ? { ...s, score: Number(e.target.value) || 1 } : s))}
                    className={FIELD_CLS} />
                </div>
              </div>

              <div className="rounded-xl px-4 py-2.5 border"
                style={{ background: 'var(--bg-soft)', borderColor: 'var(--border)' }}>
                <span className="text-sm font-medium" style={{ color: 'var(--ink-2)' }}>
                  Jami: <strong>{inlineSections[activeSection].questions.filter(q => q.content.trim()).length}</strong> savol
                  · har biri <strong>{inlineSections[activeSection].score}</strong> ball
                  · Baza avtomatik yaratiladi
                </span>
              </div>

              <div className="space-y-3">
                {inlineSections[activeSection].questions.map((q, idx) => (
                  <InlineQuestionCard key={q._id} question={q} number={idx + 1}
                    onChange={patch => setInlineSections(prev => prev.map((s, i) => i === activeSection
                      ? { ...s, questions: s.questions.map((item, qi) => qi === idx ? { ...item, ...patch } : item) }
                      : s))}
                    onRemove={() => setInlineSections(prev => prev.map((s, i) => i === activeSection
                      ? { ...s, questions: s.questions.filter((_, qi) => qi !== idx) }
                      : s))} />
                ))}
              </div>

              <button type="button"
                onClick={() => setInlineSections(prev => prev.map((s, i) => i === activeSection ? { ...s, questions: [...s.questions, newInlineQ()] } : s))}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed text-sm font-medium transition-all"
                style={{ borderColor: 'var(--border)', color: 'var(--ink-3)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--coral)'; e.currentTarget.style.color = 'var(--coral)'; e.currentTarget.style.background = 'var(--coral-tint)' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--ink-3)'; e.currentTarget.style.background = 'transparent' }}>
                <Plus size={16} /> Savol qo'shish
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 4: Ko'rib chiqish */}
      {step === 4 && (
        <div className="space-y-4">
          <div className="rounded-xl border divide-y overflow-hidden"
            style={{ borderColor: 'var(--border)', background: 'var(--bg-soft)' }}>
            <div className="px-4 py-3" style={{ borderColor: 'var(--border)' }}>
              <p className="text-xs uppercase font-medium mb-1" style={{ color: 'var(--ink-3)' }}>Imtihon nomi</p>
              <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{formValues.title}</p>
              {formValues.description && <p className="text-xs mt-1" style={{ color: 'var(--ink-3)' }}>{formValues.description}</p>}
            </div>
            <div className="px-4 py-3 flex flex-wrap gap-2" style={{ borderColor: 'var(--border)' }}>
              {formValues.is_public && <Badge variant="green">🌐 Ommaviy</Badge>}
              {formValues.leaderboard_visible && <Badge variant="amber">🏆 Reyting ommaviy</Badge>}
              {!formValues.is_public && !formValues.leaderboard_visible && <span className="text-xs" style={{ color: 'var(--ink-3)' }}>—</span>}
            </div>
            <div className="px-4 py-3 grid grid-cols-2 gap-3 text-sm" style={{ borderColor: 'var(--border)' }}>
              <div><p className="text-xs" style={{ color: 'var(--ink-3)' }}>Boshlanish</p><p className="font-medium" style={{ color: 'var(--ink)' }}>{formValues.start_time || '—'}</p></div>
              <div><p className="text-xs" style={{ color: 'var(--ink-3)' }}>Tugash</p><p className="font-medium" style={{ color: 'var(--ink)' }}>{formValues.end_time || '—'}</p></div>
              <div><p className="text-xs" style={{ color: 'var(--ink-3)' }}>Vaqt chegarasi</p><p className="font-medium" style={{ color: 'var(--ink)' }}>{formValues.time_limit_minutes ? `${formValues.time_limit_minutes} daqiqa` : '—'}</p></div>
              <div><p className="text-xs" style={{ color: 'var(--ink-3)' }}>Urinishlar</p><p className="font-medium" style={{ color: 'var(--ink)' }}>{formValues.allowed_attempts}</p></div>
            </div>
            {selectedCriteria && (
              <div className="px-4 py-3" style={{ borderColor: 'var(--border)' }}>
                <p className="text-xs uppercase font-medium mb-1" style={{ color: 'var(--ink-3)' }}>Baholash mezoni</p>
                <p className="text-sm" style={{ color: 'var(--ink)' }}>{selectedCriteria.name}</p>
              </div>
            )}
            {questionMode === 'bank' && bankConfigs.length > 0 && (
              <div className="px-4 py-3" style={{ borderColor: 'var(--border)' }}>
                <p className="text-xs uppercase font-medium mb-2" style={{ color: 'var(--ink-3)' }}>Savollar bazalari</p>
                {bankConfigs.map((bc, i) => {
                  const bank = allBanks.find(b => String(b.id) === String(bc.question_bank))
                  return bank ? (
                    <div key={i} className="flex justify-between text-sm">
                      <span style={{ color: 'var(--ink-2)' }}>{bank.name}</span>
                      <span style={{ color: 'var(--ink-3)' }}>{bc.question_count} × {bc.score_per_question} ball</span>
                    </div>
                  ) : null
                })}
                <p className="text-xs font-semibold mt-2 pt-2 border-t" style={{ color: 'var(--coral)', borderColor: 'var(--border)' }}>
                  Jami: {totalQ} savol · {totalScore} ball
                </p>
              </div>
            )}
            {questionMode === 'inline' && (() => {
              const validSections = inlineSections
                .map(sec => ({ ...sec, questions: sec.questions.filter(q => q.content.trim()) }))
                .filter(sec => sec.questions.length > 0)
              const totalInlineQ = validSections.reduce((s, sec) => s + sec.questions.length, 0)
              const totalInlineScore = validSections.reduce((s, sec) => s + sec.questions.length * (Number(sec.score) || 1), 0)
              return (
                <div className="px-4 py-3 space-y-2" style={{ borderColor: 'var(--border)' }}>
                  <p className="text-xs uppercase font-medium mb-1" style={{ color: 'var(--ink-3)' }}>
                    Savollar ({totalInlineQ} ta{validSections.length > 1 ? `, ${validSections.length} ta fan` : ''})
                  </p>
                  {validSections.map((sec, i) => {
                    const subjName = allSubjects.find(s => String(s.id) === String(sec.subject))?.name
                    const bankName = validSections.length > 1
                      ? `${formValues.title} — ${subjName || `Fan ${i + 1}`}`
                      : `${formValues.title} — Savol bazasi`
                    return (
                      <div key={sec._id} className="flex justify-between text-sm">
                        <span style={{ color: 'var(--ink-2)' }}>{bankName} <span style={{ color: 'var(--ink-3)' }}>({subjName ?? 'fan belgilanmagan'})</span></span>
                        <span style={{ color: 'var(--ink-3)' }}>{sec.questions.length} × {sec.score} ball</span>
                      </div>
                    )
                  })}
                  {validSections.length > 1 && (
                    <p className="text-xs font-semibold mt-2 pt-2 border-t" style={{ color: 'var(--coral)', borderColor: 'var(--border)' }}>
                      Jami: {totalInlineQ} savol · {totalInlineScore} ball
                    </p>
                  )}
                </div>
              )
            })()}
          </div>
          {saveError && (
            <p className="text-xs px-3 py-2 rounded-xl border break-all"
              style={{ color: 'var(--danger)', background: 'var(--danger-tint)', borderColor: 'var(--danger-soft)' }}>
              {saveError}
            </p>
          )}
        </div>
      )}

      {/* Nav */}
      <div className="flex justify-between gap-3 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
        <Button variant="secondary" type="button" onClick={() => step === 1 ? onBack() : setStep(s => s - 1)}>
          ← {step === 1 ? 'Orqaga' : 'Oldingi'}
        </Button>
        {step < 4
          ? <Button type="button" onClick={goNext}>Keyingi →</Button>
          : <Button type="button" loading={isSubmitting} onClick={handleSubmit(onSubmit)}>Imtihon yaratish</Button>
        }
      </div>
    </form>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// NationalCertForm
// ─────────────────────────────────────────────────────────────────────────────

const NAT_STEPS = ['Asosiy', 'Vaqt', 'Savollar', "Ko'rib chiqish"]
const NAT_SECTIONS = [
  { key: 'A', label: 'A qism', desc: '1–32 (MCQ, 4 variant)' },
  { key: 'B', label: 'B qism', desc: '33–35 (moslashtirish, A–F)' },
  { key: 'C', label: 'C qism', desc: '36–45 (juftlik, AI)' },
]

function NationalCertForm({ allCriteria, setCriteria, defaultCriteriaId, onSaved, onBack }) {
  const [step,       setStep]       = useState(1)
  const [section,    setSection]    = useState('A')
  const [sectionA,   setSectionA]   = useState(initSectionA)
  const [sectionB,   setSectionB]   = useState(initSectionB)
  const [sectionC,   setSectionC]   = useState(initSectionC)
  const [saveError,  setSaveError]  = useState(null)
  const [showNewCriteria, setShowNewCriteria] = useState(false)
  const [newCriteriaName, setNewCriteriaName] = useState('')
  const [savingCriteria,  setSavingCriteria]  = useState(false)

  const { register, handleSubmit, watch, trigger, setValue, formState: { isSubmitting, errors } } = useForm({
    shouldUnregister: false,
    defaultValues: {
      title: '', description: '',
      start_time: '', end_time: '',
      time_limit_minutes: 180, allowed_attempts: 1,
      grading_criteria: defaultCriteriaId != null ? String(defaultCriteriaId) : '',
    },
  })
  const formValues = watch()
  const selectedCriteria = allCriteria?.find(c => String(c.id) === String(formValues.grading_criteria))

  function updateA(idx, patch) { setSectionA(prev => prev.map((q, i) => i === idx ? { ...q, ...patch } : q)) }
  function updateC(idx, patch) { setSectionC(prev => prev.map((q, i) => i === idx ? { ...q, ...patch } : q)) }

  async function onSubmit(data) {
    setSaveError(null)
    try {
      // Build all questions in order
      const bankName = `${data.title} — Milliy sertifikat`
      const { data: bank } = await createQuestionBank({ name: bankName })

      const createPromises = []
      let order = 0

      // Section A: Q1–32 MCQ
      for (const q of sectionA) {
        const opts = q.options.map((text, i) => ({ id: i + 1, text }))
        createPromises.push(createQuestion({
          question_bank: bank.id, order: order++,
          question_type: 1,
          content: q.content || `${q.number}-savol`,
          options: opts,
          correct_answer: { answer: q.correct + 1 },
          score: q.score,
        }))
      }

      // Section B: Q33–35 (matching with shared A–F options)
      const sharedOpts = sectionB.sharedOptions.map((o, i) => ({ id: i + 1, text: o.text }))
      for (const q of sectionB.questions) {
        createPromises.push(createQuestion({
          question_bank: bank.id, order: order++,
          question_type: 1,
          content: q.content || `${q.number}-savol`,
          options: sharedOpts,
          correct_answer: { answer: q.correct + 1 },
          score: sectionB.score,
        }))
      }

      // Section C: Q36–45 (paired, AI evaluated)
      for (const q of sectionC) {
        const parts = [q.stem, q.subA ? `a) ${q.subA}` : '', q.subB ? `b) ${q.subB}` : ''].filter(Boolean)
        createPromises.push(createQuestion({
          question_bank: bank.id, order: order++,
          question_type: 4,
          content: parts.join('\n') || `${q.number}-savol`,
          options: [],
          correct_answer: { a: q.answerA, b: q.answerB },
          score: q.score,
        }))
      }

      await Promise.all(createPromises)

      const totalQuestions = sectionA.length + sectionB.questions.length + sectionC.length
      await createExam({
        ...data,
        is_national_cert:    true,
        is_public:           false,
        leaderboard_visible: false,
        show_errors:         false,
        time_limit_minutes:  data.time_limit_minutes ? Number(data.time_limit_minutes) : null,
        allowed_attempts:    Number(data.allowed_attempts),
        start_time:          data.start_time || null,
        end_time:            data.end_time || null,
        grading_criteria:    data.grading_criteria ? Number(data.grading_criteria) : null,
        bank_configs: [{
          question_bank: bank.id,
          question_count: totalQuestions,
          score_per_question: 1,
          order: 0,
        }],
      })
      onSaved()
    } catch (err) {
      setSaveError(err.response?.data ? JSON.stringify(err.response.data) : err.message)
    }
  }

  const totalScore =
    sectionA.reduce((s, q) => s + q.score, 0) +
    sectionB.questions.length * sectionB.score +
    sectionC.reduce((s, q) => s + q.score, 0)

  return (
    <form onSubmit={e => e.preventDefault()} className="space-y-6">
      <StepBar step={step} labels={NAT_STEPS} />

      {/* Badge */}
      <div className="flex items-center gap-2 -mt-2">
        <span className="text-xs px-2.5 py-1 rounded-full font-semibold"
          style={{ background: 'var(--lav-tint)', color: 'var(--lav)' }}>
          🎓 Milliy sertifikat
        </span>
        <span className="text-xs" style={{ color: 'var(--ink-3)' }}>45 savol · Reyting yo'q</span>
      </div>

      {/* Step 1: Asosiy */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="block text-sm font-medium" style={{ color: 'var(--ink-2)' }}>Imtihon nomi *</label>
            <input {...register('title', { required: true })} className={FIELD_CLS}
              placeholder="Masalan: Tarix fanidan milliy sertifikat imtihoni" />
            {errors.title && <p className="text-xs" style={{ color: 'var(--danger)' }}>Majburiy maydon</p>}
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium" style={{ color: 'var(--ink-2)' }}>Tavsif</label>
            <textarea {...register('description')} rows={3} className={FIELD_CLS}
              placeholder="Imtihon haqida qisqacha ma'lumot..." />
          </div>
          <div className="rounded-xl p-3 text-sm" style={{ background: 'var(--lav-tint)', color: 'var(--lav)' }}>
            🎓 Milliy sertifikat imtihoni uchun reyting va ommaviylik sozlamalari avtomatik o'chirilgan holda yaratiladi.
          </div>
        </div>
      )}

      {/* Step 2: Vaqt */}
      {step === 2 && (
        <TimeStep
          register={register} allCriteria={allCriteria} setCriteria={setCriteria} setValue={setValue}
          showNewCriteria={showNewCriteria} setShowNewCriteria={setShowNewCriteria}
          newCriteriaName={newCriteriaName} setNewCriteriaName={setNewCriteriaName}
          savingCriteria={savingCriteria} setSavingCriteria={setSavingCriteria}
        />
      )}

      {/* Step 3: Savollar */}
      {step === 3 && (
        <div className="space-y-4">
          {/* Score summary */}
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border"
            style={{ background: 'var(--lav-tint)', borderColor: 'var(--lav-soft)' }}>
            <span className="text-sm font-medium" style={{ color: 'var(--lav)' }}>
              Jami: <strong>45</strong> savol · <strong>{totalScore.toFixed(1)}</strong> ball
            </span>
            <span className="text-xs ml-auto" style={{ color: 'var(--ink-3)' }}>
              A: {sectionA.reduce((s, q) => s + q.score, 0)} · B: {(sectionB.score * 3).toFixed(1)} · C: {sectionC.reduce((s, q) => s + q.score, 0)}
            </span>
          </div>

          {/* Section tabs */}
          <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'var(--bg-soft)' }}>
            {NAT_SECTIONS.map(s => (
              <button key={s.key} type="button" onClick={() => setSection(s.key)}
                className="flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all"
                style={section === s.key
                  ? { background: 'var(--surface)', color: 'var(--coral)', boxShadow: 'var(--shadow-sm)' }
                  : { color: 'var(--ink-3)' }
                }
              >
                <span className="font-bold">{s.label}</span>
                <span className="block text-xs opacity-70">{s.desc}</span>
              </button>
            ))}
          </div>

          {/* Section A */}
          {section === 'A' && (
            <div className="space-y-3">
              {sectionA.map((q, idx) => (
                <NatMCQCard key={q._id} q={q} onChange={patch => updateA(idx, patch)} />
              ))}
            </div>
          )}

          {/* Section B */}
          {section === 'B' && (
            <NatMatchSection sectionB={sectionB} onChange={patch => setSectionB(prev => ({ ...prev, ...patch }))} />
          )}

          {/* Section C */}
          {section === 'C' && (
            <div className="space-y-3">
              {sectionC.map((q, idx) => (
                <NatPairedCard key={q._id} q={q} onChange={patch => updateC(idx, patch)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 4: Ko'rib chiqish */}
      {step === 4 && (
        <div className="space-y-4">
          <div className="rounded-xl border divide-y overflow-hidden"
            style={{ borderColor: 'var(--border)', background: 'var(--bg-soft)' }}>
            <div className="px-4 py-3">
              <p className="text-xs uppercase font-medium mb-1" style={{ color: 'var(--ink-3)' }}>Imtihon nomi</p>
              <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{formValues.title}</p>
              {formValues.description && <p className="text-xs mt-1" style={{ color: 'var(--ink-3)' }}>{formValues.description}</p>}
            </div>
            <div className="px-4 py-3 flex flex-wrap gap-2">
              <Badge variant="purple">🎓 Milliy sertifikat</Badge>
            </div>
            <div className="px-4 py-3 grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-xs" style={{ color: 'var(--ink-3)' }}>Boshlanish</p><p className="font-medium" style={{ color: 'var(--ink)' }}>{formValues.start_time || '—'}</p></div>
              <div><p className="text-xs" style={{ color: 'var(--ink-3)' }}>Tugash</p><p className="font-medium" style={{ color: 'var(--ink)' }}>{formValues.end_time || '—'}</p></div>
              <div><p className="text-xs" style={{ color: 'var(--ink-3)' }}>Vaqt chegarasi</p><p className="font-medium" style={{ color: 'var(--ink)' }}>{formValues.time_limit_minutes ? `${formValues.time_limit_minutes} daqiqa` : '—'}</p></div>
              <div><p className="text-xs" style={{ color: 'var(--ink-3)' }}>Urinishlar</p><p className="font-medium" style={{ color: 'var(--ink)' }}>{formValues.allowed_attempts}</p></div>
            </div>
            {selectedCriteria && (
              <div className="px-4 py-3">
                <p className="text-xs uppercase font-medium mb-1" style={{ color: 'var(--ink-3)' }}>Baholash mezoni</p>
                <p className="text-sm" style={{ color: 'var(--ink)' }}>{selectedCriteria.name}</p>
              </div>
            )}
            <div className="px-4 py-3">
              <p className="text-xs uppercase font-medium mb-2" style={{ color: 'var(--ink-3)' }}>Savol tuzilmasi</p>
              {[
                { label: 'A qism (1–32)', count: 32, score: sectionA.reduce((s, q) => s + q.score, 0) },
                { label: 'B qism (33–35)', count: 3, score: (sectionB.score * 3) },
                { label: 'C qism (36–45)', count: 10, score: sectionC.reduce((s, q) => s + q.score, 0) },
              ].map(row => (
                <div key={row.label} className="flex justify-between text-sm py-0.5">
                  <span style={{ color: 'var(--ink-2)' }}>{row.label}</span>
                  <span style={{ color: 'var(--ink-3)' }}>{row.count} savol · {row.score.toFixed(1)} ball</span>
                </div>
              ))}
              <p className="text-xs font-semibold mt-2 pt-2 border-t" style={{ color: 'var(--coral)', borderColor: 'var(--border)' }}>
                Jami: 45 savol · {totalScore.toFixed(1)} ball
              </p>
            </div>
          </div>
          {saveError && (
            <p className="text-xs px-3 py-2 rounded-xl border break-all"
              style={{ color: 'var(--danger)', background: 'var(--danger-tint)', borderColor: 'var(--danger-soft)' }}>
              {saveError}
            </p>
          )}
        </div>
      )}

      {/* Nav */}
      <div className="flex justify-between gap-3 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
        <Button variant="secondary" type="button" onClick={() => step === 1 ? onBack() : setStep(s => s - 1)}>
          ← {step === 1 ? 'Orqaga' : 'Oldingi'}
        </Button>
        {step < 4
          ? <Button type="button" onClick={async () => {
              if (step === 1) { const ok = await trigger('title'); if (!ok) return }
              setStep(s => s + 1)
            }}>Keyingi →</Button>
          : <Button type="button" loading={isSubmitting} onClick={handleSubmit(onSubmit)}>
              Imtihon yaratish
            </Button>
        }
      </div>
    </form>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

export default function CreateExamPage() {
  const navigate                     = useNavigate()
  const { user }                     = useAuth()
  const [examType,    setExamType]   = useState(null) // null | 'regular' | 'national'
  const [allBanks,    setAllBanks]   = useState([])
  const [allCriteria, setAllCriteria]= useState([])
  const [allSubjects, setAllSubjects]= useState([])
  const [loading,     setLoading]    = useState(true)

  useEffect(() => {
    Promise.all([
      fetchAllPages(listQuestionBanks),
      fetchAllPages(listGradingCriteria),
      fetchAllPages(listSubjects),
    ]).then(([banks, criteria, subjects]) => {
      setAllBanks(banks)
      setAllCriteria(criteria)
      setAllSubjects(subjects)
    }).finally(() => setLoading(false))
  }, [])

  const pageTitle = examType === 'regular' ? 'Oddiy imtihon'
    : examType === 'national' ? 'Milliy sertifikat'
    : 'Imtihon turi tanlang'

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => examType ? setExamType(null) : navigate('/exams')}
          className="p-2 rounded-xl transition-colors"
          style={{ color: 'var(--ink-3)' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-soft)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--ink)' }}>Yangi imtihon</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--ink-3)' }}>{pageTitle}</p>
        </div>
      </div>

      <div className="rounded-2xl border p-6" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        {loading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : examType === null ? (
          <ExamTypePicker onSelect={setExamType} />
        ) : examType === 'regular' ? (
          <RegularExamForm
            allBanks={allBanks}
            allSubjects={allSubjects}
            allCriteria={allCriteria}
            setCriteria={setAllCriteria}
            defaultCriteriaId={user?.default_grading_criteria}
            onSaved={() => navigate('/exams')}
            onBack={() => setExamType(null)}
          />
        ) : (
          <NationalCertForm
            allCriteria={allCriteria}
            setCriteria={setAllCriteria}
            defaultCriteriaId={user?.default_grading_criteria}
            onSaved={() => navigate('/exams')}
            onBack={() => setExamType(null)}
          />
        )}
      </div>
    </div>
  )
}
